import { Loader2 } from "lucide-react";

/* Tiny inline spinner — still useful for button-level "Saving…" states
 * where a skeleton block would be visually overkill. */
export function Spinner({ size = 18 }) {
    return <Loader2 size={size} className="animate-spin text-muted-foreground" />;
}

/* App-launch boot loader — shown while a top-level context (tenant
 * resolution, auth check) is still resolving and there's no page shell
 * to render a FullPageLoader/skeleton into yet. Previously this was a
 * bare spinner with no label (user-reported: reads as a blank/frozen
 * screen on cold launch). ``message`` is intentionally a prop, not a
 * hardcoded string, so every call site — CustomerShell's tenant-
 * resolution gate, RiderLayout's auth gate, any future top-level gate —
 * can reuse this one component instead of hand-rolling the same
 * flex-center div. Uses ``min-h-screen-safe`` (100dvh) rather than
 * ``min-h-screen`` so it isn't clipped by mobile browser chrome; the
 * global ``body`` safe-area padding (index.css) already handles the
 * status-bar / home-indicator insets for every full-screen container,
 * so no extra padding is added here. */
export function AppBootLoader({ message = "Loading, please wait…" }) {
    return (
        <div
            className="min-h-screen-safe flex flex-col items-center justify-center gap-3 bg-background"
            data-testid="app-boot-loader"
            aria-busy="true"
            aria-label={message}
        >
            <div className="skeleton w-12 h-12 rounded-full" />
            <p className="text-sm text-muted-foreground">{message}</p>
        </div>
    );
}

/* Full-page loader.
 *
 * Replaces the previous centred spinner with a content-shaped shimmer
 * skeleton so users see "the page is materialising" rather than
 * "something is broken / still spinning". Used by ~6 customer pages
 * (cart, orders, wallet, addresses, restaurant detail, order track)
 * + RequireAuth boot — all of those share a roughly list-of-cards
 * shape, so a generic skeleton works for everything without per-page
 * refactors.
 *
 * The shimmer uses the ``.shimmer`` CSS class (animated gradient
 * sweep) defined in ``index.css`` — see the ``@keyframes shimmer-sweep``
 * rule for the sweep timing. */
export function FullPageLoader() {
    return (
        <div
            className="px-4 py-5 space-y-4 max-w-3xl mx-auto"
            data-testid="full-page-loader"
            aria-busy="true"
            aria-label="Loading"
        >
            {/* Title bar */}
            <div className="shimmer h-7 w-2/3" />
            <div className="shimmer h-4 w-1/3" />

            {/* Three content blocks — list-of-cards shape */}
            <div className="space-y-3 pt-2">
                {[0, 1, 2].map((i) => (
                    <div
                        key={i}
                        className="flex gap-3 items-start p-3 rounded-2xl border border-border/40"
                    >
                        <div className="shimmer h-16 w-16 flex-shrink-0" />
                        <div className="flex-1 space-y-2 py-1">
                            <div className="shimmer h-4 w-4/5" />
                            <div className="shimmer h-3 w-3/5" />
                            <div className="shimmer h-3 w-2/5" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Bottom block — gives the skeleton a satisfying base
             * weight instead of trailing off into white space. */}
            <div className="shimmer h-24 w-full" />
        </div>
    );
}

export function CardSkeleton() {
    return (
        <div className="space-y-3">
            <div className="shimmer aspect-[4/3]" />
            <div className="shimmer h-4 w-3/4" />
            <div className="shimmer h-3 w-1/2" />
        </div>
    );
}
